import * as React from 'react'
import {
  type ComponentBlock,
  type InstanceBlock,
  type ReferenceBlock,
  type Block,
  isInstanceBlock,
  isComponentBlock,
  BlockChange,
  isReferenceBlock,
} from '@basehub/data/replicache/index'
import { invariant } from '@epic-web/invariant'
import { clsx } from 'clsx'

import type { TreeManager } from '@basehub/utils/tree/index'

import { BlockIcon } from '~/components/block-icon'
import { useDashboardSlugs } from '~/hooks/use-dashboard-slugs'
import { useTabsStore } from '../../tabs/store'
import { useRepoStore } from '../../repo-store'
import { ButtonLink } from '~/sotano-kit/primitives/button'
import { ArrowTopRightSmallIcon } from './input'
import Selector from '~/sotano-kit/primitives/selector'
import { useBranchOrCommitSegment } from '~/lib/commits/use-selected-branch-or-commit'
import { idToSourceBlockId } from '@basehub/data/replicache/mutators/repo-space/block/mutators/copy-on-write'
import { OverridesDiffWrapper } from '../../../../../components/diff/overrides-wrapper'

export type ReferenceBlockValueDiff = {
  [id: ReferenceBlock['value']['targetBlockIds'][number]]: {
    added?: boolean
    removed?: boolean
    modified?: boolean
  }
}

export const ReferenceBlockDiff = ({
  change,
  ...rest
}: {
  change: BlockChange
  block: ReferenceBlock
  blockTreeManager: TreeManager<Block, NonNullable<unknown>> | undefined
  condition: 'removed' | 'added'
}) => {
  const diff = getReferenceBlockDiff({
    previous: change.previous as ReferenceBlock,
    current: change.current as ReferenceBlock,
  })

  return (
    <>
      <PrimitiveReferenceBlockDiff
        status={change.status}
        diff={diff}
        {...rest}
      />
      <ReferenceOverridesDiff change={change} {...rest} />
    </>
  )
}

const PrimitiveReferenceBlockDiff = ({
  diff,
  status,
  block,
  blockTreeManager,
  condition,
  className,
}: {
  status: BlockChange['status']
  diff: ReferenceBlockValueDiff
  block: ReferenceBlock
  blockTreeManager: TreeManager<Block, NonNullable<unknown>> | undefined
  condition: 'removed' | 'added'
  className?: string
}) => {
  const id = React.useId()
  const { teamSlug, repoSlug } = useDashboardSlugs()
  const { encodedSegment } = useBranchOrCommitSegment()
  const addTab = useTabsStore((s) => s.addTab)
  const setFocus = useRepoStore((s) => s.setFocus)

  const targetBlocks = React.useMemo(() => {
    if (!blockTreeManager) return []

    return Object.entries(diff).reduce<
      Array<
        (InstanceBlock | ComponentBlock) & {
          className: string
          removed: boolean
        }
      >
    >((acum, [id, targetBlock]) => {
      if (targetBlock.removed && condition === 'added') return acum
      if (targetBlock.added && condition === 'removed') return acum

      const block = blockTreeManager.getNode(id)
      if (!block) return acum

      invariant(
        isInstanceBlock(block?.value) || isComponentBlock(block?.value),
        `Referenced blocks should either be instances or components, found ${block?.value.type}`
      )
      return acum.concat({
        ...block.value,
        removed: targetBlock.removed ?? false,
        className: clsx(
          'relative flex items-center before:absolute z-10 before:rounded before:-z-10 before:content-[""] before:-inset-y-0.5 before:-left-0.5 before:w-full',
          {
            'before:bg-green-diff/30': targetBlock.added,
            'before:bg-red-diff/50': targetBlock.removed,
            'before:bg-yellow/60': targetBlock.modified,
          }
        ),
      })
    }, [])
  }, [diff, blockTreeManager, condition])

  const renderResult = (search: string) => {
    if (search && !block?.constraints?.multiple) return

    if (!targetBlocks.length) {
      if (search) return

      return (
        <div className="absolute flex items-center space-x-2 leading-none text-dark-muted">
          Nothing was referenced yet
        </div>
      )
    }

    return targetBlocks.map((_block, index) => (
      <div key={index} className="relative mr-6">
        {/* we're only displaying links if this is the right panel (added) */}
        {!_block.removed ? (
          <ButtonLink
            unstyled
            href={`/${teamSlug}/${repoSlug}/explore/${encodedSegment}/${idToSourceBlockId(
              _block
            )}`}
            className={clsx(
              _block.className,
              'flex items-center space-x-2 hover:underline leading-none pr-1.5'
            )}
            onClick={() => {
              addTab({ key: _block.id, partiallyOpen: true })
              setFocus({
                type: 'block:document-title',
                blockId: idToSourceBlockId(_block),
              })
            }}
          >
            <span className="relative">
              <BlockIcon
                block={_block}
                iconProps={{ className: 'text-dark-muted' }}
              />
              <span className="absolute -bottom-0.5 -right-1">
                <ArrowTopRightSmallIcon />
              </span>
            </span>
            <span>{_block.title || 'Untitled'}</span>
          </ButtonLink>
        ) : (
          <div
            className={clsx(
              _block.className,
              'flex items-center space-x-2 leading-none pr-1.5'
            )}
          >
            <span className="relative">
              <BlockIcon
                block={_block}
                iconProps={{ className: 'text-dark-muted' }}
              />
              <span className="absolute -bottom-0.5 -right-1">
                <ArrowTopRightSmallIcon />
              </span>
            </span>
            <span>{_block.title || 'Untitled'}</span>
          </div>
        )}

        {index < targetBlocks.length - 1 && (
          <div className="absolute -right-5 top-1/2 -translate-y-1/2 text-dark-faint h-[14px] w-[14px] text-lg flex items-center justify-center">
            -
          </div>
        )}
      </div>
    ))
  }

  return (
    <Selector
      className={clsx(
        className,
        condition === 'added' && status === 'deleted' && 'opacity-30'
      )}
      search={''}
      setSearch={() => {}}
      onSelect={() => {}}
      options={() => []}
      id={`${id}-reference-diff`}
      disabled
      renderResult={renderResult}
    />
  )
}

const getReferenceBlockDiff = ({
  previous,
  current,
}: {
  previous:
    | { value: Pick<ReferenceBlock['value'], 'targetBlockIds'> }
    | null
    | undefined
  current:
    | { value: Pick<ReferenceBlock['value'], 'targetBlockIds'> }
    | null
    | undefined
}) => {
  let diff: ReferenceBlockValueDiff = {}

  const previousTargetBlockIds = previous?.value.targetBlockIds ?? []

  if (!previous) {
    invariant(current, 'Current should be defined')
    diff = current.value.targetBlockIds.reduce((acum, targetBlockId) => {
      acum[targetBlockId] = { added: true }
      return acum
    }, diff)
  } else if (!current) {
    diff = previousTargetBlockIds.reduce((acum, targetBlockId) => {
      acum[targetBlockId] = { removed: true }
      return acum
    }, diff)
  } else {
    diff = previousTargetBlockIds.reduce((acum, targetBlockId) => {
      acum[targetBlockId] = {
        removed: !current.value.targetBlockIds.includes(targetBlockId),
      }
      return acum
    }, diff)

    diff = current.value.targetBlockIds.reduce((acum, targetBlockId) => {
      if (acum[targetBlockId]) return acum
      acum[targetBlockId] = {
        added: !previousTargetBlockIds.includes(targetBlockId),
      }

      return acum
    }, diff)
  }
  return diff
}

type OverrideDiff = {
  overrideKey: string
  className?: string
  diff: ReferenceBlockValueDiff
}

const ReferenceOverridesDiff = ({
  change,
  condition,
  block,
  blockTreeManager,
}: {
  change: BlockChange
  block: ReferenceBlock
  blockTreeManager: TreeManager<Block, NonNullable<unknown>> | undefined
  condition: 'removed' | 'added'
}) => {
  const blockRef = React.useRef(block)
  const blockTreeManagerRef = React.useRef(blockTreeManager)
  const overrides = React.useMemo(() => {
    invariant(
      !change.current || isReferenceBlock(change.current),
      'Current should be a reference block'
    )

    if (!change.previous) return []
    if (!isReferenceBlock(change.previous)) return []

    const iterator =
      condition === 'removed'
        ? change.previous?.variantOverrides
        : change.current?.variantOverrides
    const _overrides: OverrideDiff[] = []
    for (const [key, value] of Object.entries(iterator ?? {})) {
      const { previous, current } =
        condition === 'removed'
          ? {
              previous: value satisfies {
                value: Pick<ReferenceBlock['value'], 'targetBlockIds'>
              },
              current: change.current?.variantOverrides?.[key] satisfies
                | { value: Pick<ReferenceBlock['value'], 'targetBlockIds'> }
                | undefined,
            }
          : {
              previous: change.previous?.variantOverrides?.[key] satisfies
                | { value: Pick<ReferenceBlock['value'], 'targetBlockIds'> }
                | undefined,
              current: value satisfies {
                value: Pick<ReferenceBlock['value'], 'targetBlockIds'>
              },
            }
      const diff = getReferenceBlockDiff({ previous, current })

      _overrides.push({
        overrideKey: key,
        diff,
      })
    }

    return _overrides.sort((a, b) => a.overrideKey.localeCompare(b.overrideKey))
  }, [change, condition])

  const Children = React.useCallback(
    (props: OverrideDiff) => {
      return (
        <PrimitiveReferenceBlockDiff
          status={change.status}
          condition={condition}
          block={blockRef.current}
          blockTreeManager={blockTreeManagerRef.current}
          {...props}
        />
      )
    },
    [change.status, condition]
  )

  if (overrides.length === 0) return null

  return (
    <OverridesDiffWrapper overrides={overrides}>
      {Children}
    </OverridesDiffWrapper>
  )
}
